import { inject } from '@angular/core';
import type { CanActivateFn, UrlTree } from '@angular/router';
import { Router } from '@angular/router';
import { map, type Observable, of, switchMap } from 'rxjs';

import { AUTH_HANDLER } from '../../features/auth/tokens/auth.tokens';
// Deroga guard/token (`CLAUDE.md` §Barrel File Policy → Deroga guard/token): il token si prende dal file
// dei token della feature, non dal suo barrel, che esporta anche le rotte.
import { ACTIVE_CONVERSATION_HANDLER } from '../../features/onboarding/tokens/onboarding.tokens';
// Stessa deroga: la forma del parametro del QR sta accanto ai token, nel modello della feature.
import {
  PRESET_FLOW_QUERY_PARAM,
  PRESET_FLOW_ROUTE_PARAM,
  readPresetFlowId,
} from '../../features/onboarding/models/preset-flow';
import { PORTAL_AVAILABILITY } from '../tokens/app.tokens';
import { visitorOf } from './visitor';

/**
 * Guardia della radice dell'applicazione. Il portale non chiede mai credenziali all'ingresso: chi arriva
 * senza sessione entra nel percorso anonimo del Cittadino (onboarding) e trova in intestazione i comandi
 * di accesso e registrazione; chi si e' autenticato va direttamente alla lista delle sessioni —
 * l'ambito (proprie sessioni o tutte, per l'Operatore) lo risolve il BFF sull'identita' del cookie.
 *
 * La visita anonima non conta come un accesso, e la domanda e' `isSignedIn()` e non `isAuthenticated()`:
 * il biglietto della visita e' un cookie come l'altro, e chi era entrato con la sola lingua si ritrovava
 * nella lista delle sessioni — un'area di chi ha dichiarato chi e'.
 *
 * Chi arriva qui con una conversazione gia' aperta e la visita ancora viva ci torna dentro. Ricaricare la
 * pagina e' un gesto che capita per sbaglio, e finora chiudeva la visita: si ripartiva dalla scelta della
 * lingua con la propria conversazione ancora aperta dall'altra parte e nessun modo di rientrarci. Se la
 * conversazione sia ancora da riprendere lo decide la guardia della chat (`citizenConversationGuard`).
 * Senza visita — il biglietto e' scaduto, il cittadino torna dopo giorni — il segnalibro non apre niente:
 * si butta, e si riparte dalla lingua.
 *
 * In ogni altro caso la visita si chiude: chi riparte riceve un biglietto nuovo alla scelta della lingua, con la sua finestra intera, invece di portarsi dietro quello di prima che puo' scadere a
 * meta' conversazione.
 *
 * Chi arriva col flow gia' scelto — il QR dello sportello, `?flow=<id>` o `/flow/<id>` — va alla sola
 * scelta della lingua, col flow al seguito. Inquadrare un QR e' un gesto deliberato: vale piu' di una
 * conversazione rimasta aperta, che non si riprende. Chi arriva senza — l'indirizzo nudo — trova l'invito
 * a inquadrare il QR esposto allo sportello: il servizio lo sceglie il QR, e senza non c'e' niente da
 * aprire.
 *
 * Se la sonda non ha avuto risposta la guardia non sceglie niente: «anonimo» sarebbe una supposizione, e
 * mandare nel percorso del cittadino una postazione che il BFF non ha potuto riconoscere e' peggio che
 * restare fermi sull'avviso di indisponibilita'.
 */
export const entryGuard: CanActivateFn = (route) => {
  const authHandler = inject(AUTH_HANDLER);
  const activeConversation = inject(ACTIVE_CONVERSATION_HANDLER);
  const router = inject(Router);
  const availability = inject(PORTAL_AVAILABILITY);

  // Ricominciare vuol dire lasciare tutto della visita di prima: il segnalibro e il biglietto.
  const restartAt = (target: UrlTree): Observable<UrlTree> => {
    activeConversation.forget();
    return authHandler.endAnonymousVisit().pipe(map(() => target));
  };

  return visitorOf(authHandler).pipe(
    switchMap((visitor) => {
      if (availability.status() === 'unavailable') return of(false);
      if (visitor === 'signed-in') return of(router.createUrlTree(['/sessions']));

      const presetFlowId = readPresetFlowId(
        route.paramMap.get(PRESET_FLOW_ROUTE_PARAM) ??
          route.queryParamMap.get(PRESET_FLOW_QUERY_PARAM),
      );
      if (presetFlowId) {
        return restartAt(
          router.createUrlTree(['/onboarding', 'language'], {
            queryParams: { [PRESET_FLOW_QUERY_PARAM]: presetFlowId },
          }),
        );
      }

      const openConversation = activeConversation.current();
      if (openConversation && visitor === 'visit') {
        return of(router.createUrlTree(['/chat', openConversation]));
      }

      // Senza QR non c'e' un servizio da aprire: la scelta della lingua non porterebbe da nessuna parte.
      return restartAt(router.createUrlTree(['/onboarding', 'scan-qr']));
    }),
  );
};
